import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserRound, ArrowRight } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as inspectorApi from '@/api/inspector.api';
import type { PersonaPorConocer, RelacionDePersona } from '@/api/inspector.api';

/**
 * "¿QUIÉN ES?" — la tarjeta de Hoy donde Athena pregunta por la gente.
 *
 * Máximo 3 por día, solo personas con una señal, una sola vez cada una. Las
 * reglas viven en el servidor (`quien-es.service.ts`); esta tarjeta solo las
 * muestra. Si no hay nadie por conocer, no se dibuja: una tarjeta vacía le
 * roba sitio a lo que sí importa.
 *
 * Se contesta con toques, sin teclado: qué es para ti y cada cuánto quieres
 * saber de esa persona. El nombre es opcional y viene sugerido con el de su
 * perfil de WhatsApp, que es lo que casi siempre uno escribiría.
 */

const RELACIONES: Array<{ id: RelacionDePersona; nombre: string }> = [
  { id: 'cliente', nombre: 'Cliente' },
  { id: 'equipo', nombre: 'Equipo' },
  { id: 'familia', nombre: 'Familia' },
  { id: 'proveedor', nombre: 'Proveedor' },
  { id: 'amigo', nombre: 'Amigo' },
  { id: 'otro', nombre: 'Otro' },
];

const CADENCIAS: Array<{ dias: number | null; nombre: string }> = [
  { dias: 7, nombre: 'Cada semana' },
  { dias: 30, nombre: 'Cada mes' },
  { dias: null, nombre: 'Cuando escriba' },
];

function Chip({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        // macOS 27: elegido lleno del acento, el resto en gris 8 %; 44 en el teléfono, 24 en la computadora.
        'min-h-11 rounded-full px-3 text-sm transition-colors lateral:min-h-6 lateral:rounded-md lateral:px-2.5',
        activo
          ? 'bg-acento-lleno text-acento-sobre'
          : 'bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
      )}
    >
      {children}
    </button>
  );
}

function Pregunta({ p, onListo }: { p: PersonaPorConocer; onListo: () => void }) {
  const toast = useToast();
  const navegar = useNavigate();
  const [relacion, setRelacion] = useState<RelacionDePersona | null>(null);
  const [cadencia, setCadencia] = useState<number | null | undefined>(undefined);
  const [nombre, setNombre] = useState(p.sugerencia ?? '');

  const contestar = useMutation({
    mutationFn: (r: Parameters<typeof inspectorApi.identificarPersona>[1]) => inspectorApi.identificarPersona(p.contactId, r),
    onSuccess: (_d, r) => {
      toast.success(r.sinSeguimiento ? 'Listo, no te vuelvo a preguntar por esta persona.' : 'Anotado.');
      onListo();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-2.5 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{p.comoSeVe}</p>
          <p className="text-xs text-slate-500">{p.porQue}</p>
        </div>
        <button
          type="button"
          onClick={() => navegar(`/conversations/${p.conversationId}`)}
          className="-my-3 flex min-h-11 shrink-0 items-center gap-1 text-xs text-acento-texto hover:underline lateral:my-0 lateral:min-h-0"
        >
          Ver chat <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <label htmlFor={`nombre-${p.contactId}`} className="sr-only">Nombre</label>
      <input
        id={`nombre-${p.contactId}`}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder="¿Cómo se llama? (opcional)"
        className="h-11 w-full rounded-[10px] border-0 bg-white px-3 text-base text-slate-900 caret-acento shadow-[0_0_0_1px_var(--mac-campo-filo)] placeholder:text-slate-400 focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 dark:bg-white/5 dark:text-slate-100 lateral:h-6 lateral:rounded-[5px] lateral:px-2 lateral:text-sm"
      />

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Qué es para ti">
        {RELACIONES.map((r) => (
          <Chip key={r.id} activo={relacion === r.id} onClick={() => setRelacion(r.id)}>{r.nombre}</Chip>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Cada cuánto quieres saber de esta persona">
        {CADENCIAS.map((c) => (
          <Chip key={c.nombre} activo={cadencia === c.dias} onClick={() => setCadencia(c.dias)}>{c.nombre}</Chip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        <button
          type="button"
          disabled={!relacion || contestar.isPending}
          onClick={() =>
            contestar.mutate({
              relacion: relacion!,
              ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
              ...(cadencia !== undefined ? { cadenciaDias: cadencia } : {}),
            })
          }
          className="min-h-11 rounded-lg bg-acento-lleno px-4 text-sm text-acento-sobre disabled:opacity-40 lateral:min-h-6 lateral:rounded-md"
        >
          Guardar
        </button>
        <button
          type="button"
          disabled={contestar.isPending}
          onClick={() => contestar.mutate({ sinSeguimiento: true })}
          className="min-h-11 rounded-lg px-3 text-sm text-slate-700 hover:bg-[var(--mac-btn)] dark:text-slate-200 lateral:min-h-6 lateral:rounded-md"
        >
          No me importa
        </button>
      </div>
    </div>
  );
}

export function QuienEs() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['por-conocer'], queryFn: inspectorApi.getPorConocer });
  const personas = q.data?.personas ?? [];
  if (q.isPending || personas.length === 0) return null;

  const listo = () => {
    void qc.invalidateQueries({ queryKey: ['por-conocer'] });
    void qc.invalidateQueries({ queryKey: ['salud'] });
  };

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start gap-2">
          <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-300" />
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              {personas.length === 1 ? '¿Quién es esta persona?' : `¿Quiénes son? (${personas.length} de hoy)`}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              Con eso sé con quién te importa no perder el contacto. Máximo 3 por día.
            </p>
          </div>
        </div>
        {personas.map((p) => <Pregunta key={p.contactId} p={p} onListo={listo} />)}
      </CardBody>
    </Card>
  );
}
